// softkbd.js - the on-screen MS-2401H keyboard.
//
// The MS-2401H uses a bespoke Chinese word-processor keyboard matrix. This
// renderer presents the physical deck while each key still carries its true
// (Y, X) matrix wiring. The visual order is intentionally not scan order.

const MIN_HOLD_MS = 250;
const LATCH_HOLD_MS = 520;
const COMPACT_KEYS = '(max-width: 820px)';

const ASCII_LABELS = new Map([
  ['半角', 'WIDTH'],
  ['辅助', 'BKSP'],
  ['删行', 'DEL'],
  ['换页', 'FEED'],
  ['大写', 'CAPS'],
  ['换行', 'RET'],
  ['上档', 'SHIFT'],
  ['常用字', 'COMMON'],
  ['取消', 'ESC'],
  ['区位', 'IME'],
  ['英数', 'A/Z'],
  ['编码', 'PINYIN'],
  ['通信', 'PRINT'],
  ['数字', 'NUM'],
  ['拷屏', 'PRTSC'],
  ['选单', 'MENU'],
  ['重排', 'RELAY'],
  ['执行', 'EXEC'],
]);

const p = (text, tone = 'main') => ({ text, tone });

const k = (cn, en, y, x, extra = {}) => ({
  cn,
  en,
  y,
  x,
  aliases: [],
  parts: null,
  ...extra,
});

const cellOf = (key) => `${key.y},${key.x}`;

const partsForTextKey = (top, main, bottom, tones = {}) => {
  const out = [];
  if (top) out.push(p(top, tones.top || 'top'));
  if (main) out.push(p(main, tones.main || 'main'));
  if (bottom) out.push(p(bottom, tones.bottom || 'hint'));
  return out;
};

const alpha = (latin, red, roman, y, x, extra = {}) =>
  k(latin, roman, y, x, {
    face: 'alpha',
    legend: { latin, red, hint: roman },
    ...extra,
  });

const symbolKey = (main, top, red, y, x, extra = {}) =>
  k(main, top, y, x, {
    face: 'symbol',
    legend: { main, top, red },
    ...extra,
  });

const commandKey = (cn, en, y, x, extra = {}) =>
  k(cn, en, y, x, {
    parts: [p(cn, extra.primaryTone || 'command'), en ? p(en, extra.subTone || 'green') : null]
      .filter(Boolean),
    ...extra,
  });

const gridRow = (keys, spans) => {
  let col = 0;
  for (let i = 0; i < keys.length; i++) {
    keys[i].col = col;
    keys[i].span = spans[i];
    col += spans[i];
  }
  return keys;
};

const CENTER_ALT_STRIP = gridRow([
  k('印刷设定', '', -1, -1),
  k('文始', '', -1, -1),
  k('文末', '', -1, -1),
  k('字号', '', -1, -1),
  k('装饰', '', -1, -1),
  k('段製作', '', -1, -1),
  k('造字', '', -1, -1),
  k('找字', '', -1, -1),
  k('换字', '', -1, -1),
  k('词组头', '', -1, -1),
  k('词组尾', '', -1, -1),
  k('词组登记', '', -1, -1),
  k('排版设定', '', -1, -1),
  k('格式设定', '', -1, -1),
  k('全文删除', '', -1, -1),
], [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4]);

const RIGHT_ALT_STRIP = [
  k('文件存取', '', -1, -1),
  k('', '', -1, -1),
  k('', '', -1, -1),
  k('', '', -1, -1),
];

const FUNCTION_KEYS = [
  k('F1', '', 0, 8, { fn: true }), k('F2', '', 1, 8, { fn: true }),
  k('F3', '', 2, 8, { fn: true }), k('F4', '', 3, 8, { fn: true }),
  k('F5', '', 4, 8, { fn: true }), k('F6', '', 5, 8, { fn: true }),
  k('F7', '', 6, 8, { fn: true }), k('F8', '', 7, 8, { fn: true }),
  k('F9', '', 8, 8, { fn: true }), k('F10', '', 9, 8, { fn: true }),
];

const ROW_NUM = gridRow([
  commandKey('半角', '全角', 7, 2, {
    aliases: ['半角全角'],
    parts: [p('半角', 'green'), p('全角', 'green')],
  }),
  symbolKey('1', '!', '下', 0, 1),
  symbolKey('2', '@', '也', 0, 2),
  symbolKey('3', '#', '年', 0, 3),
  symbolKey('4', '%', '发', 0, 4),
  symbolKey('5', '%', '可', 0, 5),
  symbolKey('6', '^', '会', 0, 6),
  symbolKey('7', '&', '主', 0, 7),
  symbolKey('8', '*', '动', 1, 0),
  symbolKey('9', '(', '工', 1, 1),
  symbolKey('0', ')', '能', 0, 0),
  symbolKey('-', '→', '产', 1, 2),
  symbolKey('=', '←', '而', 1, 3),
  commandKey('辅助', '后退', 7, 5, { aliases: ['补助', '补助后退', '辅助后退', 'BKSP'] }),
  k('删行', '删字', 7, 1, {
    small: true,
    aliases: ['删除', '删行删亭', 'DEL'],
    parts: [p('删行', 'command'), p('删字', 'command')],
  }),
], [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4]);

const ROW_Q = gridRow([
  k('TAB', '', 7, 3, {
    aliases: ['Tab'],
    parts: [p('←', 'green'), p('→', 'green')],
  }),
  alpha('Q', '就', 'er', 4, 6),
  alpha('W', '他', 'ei', 5, 4),
  alpha('E', '要', 'e', 3, 2),
  alpha('R', '大', 'en', 4, 7),
  alpha('T', '这', 'eng', 5, 1),
  alpha('Y', '人', 'ong/iang', 5, 6),
  alpha('U', '中', 'ch', 5, 2),
  alpha('I', '国', 'sh', 3, 6),
  alpha('O', '时', 'uo', 4, 4),
  alpha('P', '出', 'ou', 4, 5),
  symbolKey('[', '“', '说', 1, 5),
  symbolKey(']', '”', '种', 1, 6),
  commandKey('换页', '走纸', 7, 0, { aliases: ['换页定纸'] }),
], [8, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4]);

const ROW_HOME = gridRow([
  commandKey('控制', 'CTRL', 6, 1, { mod: 'ctrl' }),
  symbolKey('`', '~', '子', 2, 1),
  alpha('A', '于', 'zh', 2, 6),
  alpha('S', '和', 'ai', 5, 0),
  alpha('D', '了', 'ao', 3, 1),
  alpha('F', '在', 'an', 3, 3),
  alpha('G', '一', 'ang', 3, 4),
  alpha('H', '的', 'uang/iang', 3, 5),
  alpha('J', '是', 'ian', 3, 7),
  alpha('K', '不', 'iao', 4, 0),
  alpha('L', '有', 'in', 4, 1),
  alpha(';', '地', 'ing', 1, 7),
  symbolKey("'", '"', '过', 2, 0),
  k('换行', 'RETURN', 7, 7, {
    enter: true,
    aliases: ['ENTER'],
    parts: [p('换行', 'command')],
  }),
], [4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 8]);

const ROW_Z = gridRow([
  commandKey('上档', 'SHIFT', 6, 0, { mod: 'shift' }),
  commandKey('大写', '锁定', 6, 2, { aliases: ['大写锁定', 'CAPS', 'Caps Lock'] }),
  alpha('Z', '对', 'un', 5, 7),
  alpha('X', '作', 'ua', 5, 5),
  alpha('C', '生', 'uan', 3, 0),
  alpha('V', '个', 'ü/iu', 5, 3),
  alpha('B', '上', 'ia', 2, 7),
  alpha('N', '为', 'iu', 4, 3),
  alpha('M', '从', 'ie', 4, 2),
  symbolKey(',', '<', '来', 2, 2),
  symbolKey('.', '>', '到', 2, 3),
  symbolKey('/', '?', '分', 2, 4),
  symbolKey('\\', '|', '面', 1, 4),
  commandKey('上档', 'SHIFT', 6, 0, { mod: 'shift' }),
], [5, 6, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 4, 5]);

const ROW_BOTTOM = gridRow([
  commandKey('常用字', '', 6, 4, { latch: true }),
  commandKey('取消', '命令', 7, 4, { aliases: ['取消命令', 'ESC'], dualLatch: true }),
  commandKey('区位', '电报', 7, 6, { aliases: ['区位电报', 'IME'] }),
  k('空格', 'SPACE', 8, 0, { space: true, parts: [] }),
  commandKey('英数', '标点', 6, 3, { aliases: ['英数标点', '宾数标点'] }),
  commandKey('编码', '拼音', 8, 1, { aliases: ['编码拼音', 'PINYIN'] }),
  commandKey('常用字', '', 6, 4, { latch: true }),
], [6, 4, 4, 33, 4, 4, 5]);

const MAIN_ROWS = [ROW_NUM, ROW_Q, ROW_HOME, ROW_Z, ROW_BOTTOM];

const RIGHT_TOP = [
  commandKey('通信', '印刷', 8, 3, { aliases: ['通信印刷'] }),
  commandKey('数字', '锁定', 9, 3, { aliases: ['数字锁定', 'NUMLOCK'] }),
  k('拷屏', '*', 10, 3, {
    print: true,
    parts: [p('*', 'top'), p('拷屏', 'command')],
    aliases: ['拷屏', 'PRINT', 'PRTSC', 'PRINT SCREEN'],
  }),
  k('选单', "'", 10, 5, {
    parts: [p("'", 'top'), p('选单', 'command')],
    aliases: ['选字', 'MENU'],
  }),
];

const RIGHT_PAD = [
  k('7', '开始', 9, 5, { parts: partsForTextKey('', '7', '开始', { main: 'padnum', bottom: 'green' }) }),
  k('8', '↑', 8, 5, { parts: partsForTextKey('', '8', '↑', { main: 'padnum', bottom: 'arrow' }) }),
  k('9', '上页', 9, 4, { parts: partsForTextKey('', '9', '上页', { main: 'padnum', bottom: 'green' }) }),
  k('-', '时间', 10, 6, { parts: partsForTextKey('', '-', '时间', { main: 'padnum', bottom: 'green' }) }),

  k('4', '←', 8, 4, { parts: partsForTextKey('', '4', '←', { main: 'padnum', bottom: 'arrow' }) }),
  k('5', '', 9, 7, { parts: [p('5', 'padnum')] }),
  k('6', '→', 8, 7, { parts: partsForTextKey('', '6', '→', { main: 'padnum', bottom: 'arrow' }) }),
  k('+', '词组', 10, 7, { parts: partsForTextKey('', '+', '词组', { main: 'padnum', bottom: 'green' }) }),

  k('1', '屏末', 9, 6, { parts: partsForTextKey('', '1', '屏末', { main: 'padnum', bottom: 'green' }) }),
  k('2', '↓', 8, 6, { parts: partsForTextKey('', '2', '↓', { main: 'padnum', bottom: 'arrow' }) }),
  k('3', '下页', 9, 2, { parts: partsForTextKey('', '3', '下页', { main: 'padnum', bottom: 'green' }) }),
  k('=', '计算', 10, 4, { parts: partsForTextKey('', '=', '计算', { main: 'padnum', bottom: 'green' }) }),

  k('0', '插入', 9, 1, { parts: partsForTextKey('', '0', '插入', { main: 'padnum', bottom: 'green' }) }),
  k('重排', '', 9, 0, { parts: [p('•', 'green-dot'), p('重排', 'green')] }),
  k('·', '', 10, 0, { parts: [p('·', 'green-dot')] }),
  commandKey('执行', 'EXEC', 10, 1, { primaryTone: 'command' }),
];

export class SoftKeyboard {
  constructor(machine) {
    this.machine = machine;
    this.reduceMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this._active = new Map();
    this._latched = new Map();
    this._byCell = new Map();
    this._byEl = new WeakMap();
    this._keyInfos = [];

    this.el = this._build();
    this._visible = this._loadVisible();
    this._applyVisible();
  }

  _build() {
    const panel = document.createElement('section');
    panel.className = 'soft-kbd';
    panel.setAttribute('aria-label', 'On-screen keyboard');
    if (this.reduceMotion) panel.classList.add('no-motion');

    const inner = document.createElement('div');
    inner.className = 'skbd-inner';
    inner.appendChild(this._altStrip());

    const deck = document.createElement('div');
    deck.className = 'skbd-deck';

    const fnBlock = document.createElement('div');
    fnBlock.className = 'skbd-function-block';
    const fnGrid = document.createElement('div');
    fnGrid.className = 'skbd-fn-grid';
    for (const key of FUNCTION_KEYS) fnGrid.appendChild(this._buildKey(key));
    fnBlock.appendChild(fnGrid);
    deck.appendChild(fnBlock);

    const main = document.createElement('div');
    main.className = 'skbd-alpha-block';
    for (const row of MAIN_ROWS) main.appendChild(this._row(row, 'skbd-alpha-row'));
    deck.appendChild(main);

    const numToggle = document.createElement('button');
    numToggle.type = 'button';
    numToggle.className = 'skbd-numpad-toggle';
    numToggle.setAttribute('aria-expanded', 'false');
    numToggle.setAttribute('aria-controls', 'skbd-numpad');
    numToggle.innerHTML = '<span class="label"><b>数字键盘</b></span>';
    deck.appendChild(numToggle);

    const right = document.createElement('div');
    right.className = 'skbd-right-block';
    right.id = 'skbd-numpad';
    right.appendChild(this._grid(RIGHT_TOP, 4, 'skbd-right-top'));
    right.appendChild(this._grid(RIGHT_PAD, 4, 'skbd-right-pad'));
    deck.appendChild(right);

    numToggle.addEventListener('click', () => {
      const open = !panel.classList.contains('is-numpad-open');
      panel.classList.toggle('is-numpad-open', open);
      numToggle.setAttribute('aria-expanded', String(open));
    });

    inner.appendChild(deck);
    panel.appendChild(inner);
    return panel;
  }

  _altStrip() {
    const strip = document.createElement('div');
    strip.className = 'skbd-alt-strip';
    strip.setAttribute('aria-hidden', 'true');

    const spacer = document.createElement('div');
    spacer.className = 'skbd-alt-fn-spacer';
    strip.appendChild(spacer);

    const center = document.createElement('div');
    center.className = 'skbd-alt-center';
    for (const key of CENTER_ALT_STRIP) center.appendChild(this._altLabel(key, true));
    strip.appendChild(center);

    const right = document.createElement('div');
    right.className = 'skbd-alt-right';
    for (const key of RIGHT_ALT_STRIP) right.appendChild(this._altLabel(key, false));
    strip.appendChild(right);
    return strip;
  }

  _altLabel(key, positioned) {
    const label = document.createElement('span');
    label.className = key.cn ? 'skbd-alt-label' : 'skbd-alt-label is-empty';
    label.textContent = key.cn || '';
    if (positioned) label.style.gridColumn = `${key.col + 1} / span ${key.span}`;
    return label;
  }

  _row(keys, cls = 'skbd-row') {
    const rowEl = document.createElement('div');
    rowEl.className = `skbd-row ${cls}`;
    for (const key of keys) rowEl.appendChild(this._buildKey(key));
    return rowEl;
  }

  _grid(keys, cols, cls = '') {
    const grid = document.createElement('div');
    grid.className = `skbd-grid ${cls}`.trim();
    grid.style.setProperty('--cols', String(cols));
    for (const key of keys) grid.appendChild(this._buildKey(key));
    return grid;
  }

  _buildKey(key) {
    if (key.gap) {
      const gap = document.createElement('div');
      gap.className = 'skbd-key is-gap';
      gap.setAttribute('aria-hidden', 'true');
      return gap;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'skbd-key';
    if (key.fn) btn.classList.add('is-fn');
    if (key.mod) btn.classList.add('is-mod');
    if (key.mod || key.latch || key.dualLatch) {
      btn.classList.add('is-latchable');
      btn.setAttribute('aria-pressed', 'false');
    }
    if (key.dualLatch) btn.classList.add('is-dual-latch');
    if (key.enter) btn.classList.add('is-enter');
    if (key.print) btn.classList.add('is-print');
    if (key.small) btn.classList.add('is-small');
    if (key.space) btn.classList.add('is-space');
    if (key.face) btn.classList.add(`is-${key.face}-face`);
    if (key.face === 'alpha' && key.legend.hint && key.legend.hint.length > 4) {
      btn.classList.add('has-long-hint');
    }
    if (key.w) btn.style.setProperty('--w', String(key.w));
    else if (key.span) btn.style.setProperty('--w', String(key.span / 4));
    btn.dataset.pop = this._popText(key);
    btn.dataset.mobile = this._mobileText(key);

    if (key.face === 'alpha') {
      this._appendFace(btn, [
        ['alpha-red', key.legend.red],
        ['alpha-main', key.legend.latin],
        ['alpha-hint', key.legend.hint],
      ]);
    } else if (key.face === 'symbol') {
      this._appendFace(btn, [
        ['symbol-top', key.legend.top],
        ['symbol-red', key.legend.red],
        ['symbol-main', key.legend.main],
      ]);
    } else {
      const parts = key.parts === null ? [p(key.cn, 'main'), key.en ? p(key.en, 'hint') : null].filter(Boolean) : key.parts;
      for (const part of parts) {
        const span = document.createElement('span');
        span.className = `skcap-line skcap-${part.tone}`;
        span.textContent = part.text;
        btn.appendChild(span);
      }
    }

    btn.setAttribute('aria-label', this._labelText(key));
    btn.dataset.y = String(key.y);
    btn.dataset.x = String(key.x);
    if (key.span) btn.style.gridColumn = `${key.col + 1} / span ${key.span}`;

    const info = { el: btn, key };
    this._keyInfos.push(info);
    this._byCell.set(cellOf(key), info);
    this._byEl.set(btn, info);
    this._wireKey(btn, key);
    return btn;
  }

  _appendFace(btn, specs) {
    for (const [cls, text] of specs) {
      if (!text) continue;
      const span = document.createElement('span');
      span.className = `skcap-face ${cls}`;
      span.textContent = cls === 'alpha-hint' ? String(text).replace('/', '\n') : text;
      btn.appendChild(span);
    }
  }

  _labelText(key) {
    return [...this._labelsFor(key)].join(' ');
  }

  _labelsFor(key) {
    const labels = new Set();
    for (const value of [key.cn, key.en, ...(key.aliases || [])]) {
      if (value) labels.add(String(value));
    }
    if (Array.isArray(key.parts)) {
      for (const part of key.parts) if (part.text) labels.add(String(part.text));
    }
    if (key.legend) {
      for (const value of Object.values(key.legend)) if (value) labels.add(String(value));
    }
    return labels;
  }

  _popText(key) {
    if (key.face === 'alpha') return key.legend.latin;
    if (key.face === 'symbol') return key.legend.main;
    if (key.fn) return key.cn;
    if (key.space) return '空格';
    if (Array.isArray(key.parts)) {
      const main = key.parts.find((part) => part.tone === 'command' || part.tone === 'main' || part.tone === 'padnum');
      if (main?.text) return main.text;
      const first = key.parts.find((part) => part.text);
      if (first?.text) return first.text;
    }
    return key.cn || key.en || '';
  }

  _mobileText(key) {
    if (key.face === 'alpha') return key.legend.latin;
    if (key.face === 'symbol') return key.legend.main;
    if (key.fn) return key.cn;
    if (key.space) return 'SPACE';
    const mapped = ASCII_LABELS.get(key.cn);
    if (mapped) return mapped;
    if (key.en && /^[\x20-\x7e]+$/.test(key.en)) return key.en;
    const alias = (key.aliases || []).find((value) => /^[\x20-\x7e]+$/.test(String(value)));
    if (alias) return String(alias).replace(/\s+/g, ' ');
    if (Array.isArray(key.parts)) {
      const part = key.parts.find((p) => /^[\x20-\x7e]+$/.test(String(p.text || '')));
      if (part) return String(part.text);
    }
    return '';
  }

  _wireKey(btn, key) {
    if (key.mod || key.latch) {
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this._toggleLatch(btn, key);
      });
      return;
    }

    if (key.dualLatch) {
      this._wireDualLatch(btn, key);
      return;
    }

    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      try { btn.setPointerCapture(e.pointerId); } catch { /* older engines */ }
      if (this._useTouchPicker(e)) this._beginPickedMomentary(btn, key, e.pointerId);
      else this._pressMomentary(btn, key, e.pointerId);
    });
    btn.addEventListener('pointermove', (e) => this._movePickedMomentary(e));
    const up = (e) => this._releaseMomentary(e.pointerId);
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', (e) => this._cancelMomentary(e.pointerId));
    btn.addEventListener('pointerleave', (e) => {
      if (!btn.hasPointerCapture || !btn.hasPointerCapture(e.pointerId)) {
        this._cancelMomentary(e.pointerId);
      }
    });
  }

  _useTouchPicker(e) {
    return e.pointerType === 'touch' &&
      typeof window !== 'undefined' &&
      window.matchMedia(COMPACT_KEYS).matches;
  }

  _beginPickedMomentary(btn, key, pointerId = -1) {
    if (this._active.has(pointerId)) return;
    btn.classList.add('is-down');
    this._active.set(pointerId, { kind: 'pendingMomentary', btn, key });
  }

  _movePickedMomentary(e) {
    const entry = this._active.get(e.pointerId);
    if (!entry || entry.kind !== 'pendingMomentary') return;
    const info = this._keyInfoFromPoint(e.clientX, e.clientY);
    if (!info || info.el === entry.btn) return;
    if (info.key.mod || info.key.latch || info.key.dualLatch) return;
    entry.btn.classList.remove('is-down');
    info.el.classList.add('is-down');
    entry.btn = info.el;
    entry.key = info.key;
  }

  _keyInfoFromPoint(x, y) {
    const el = document.elementFromPoint(x, y);
    const btn = el && el.closest ? el.closest('.skbd-key:not(.is-gap)') : null;
    return btn ? this._byEl.get(btn) : null;
  }

  _wireDualLatch(btn, key) {
    btn.addEventListener('contextmenu', (e) => e.preventDefault());
    btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (e.button === 2) {
        this._toggleLatch(btn, key);
        return;
      }
      if (this._active.has(e.pointerId)) return;
      try { btn.setPointerCapture(e.pointerId); } catch { /* older engines */ }
      btn.classList.add('is-down');
      const pointerId = e.pointerId;
      const timer = window.setTimeout(() => {
        const entry = this._active.get(pointerId);
        if (!entry || entry.kind !== 'dualLatch') return;
        this._active.delete(pointerId);
        entry.btn.classList.remove('is-down');
        try { entry.btn.releasePointerCapture(pointerId); } catch { /* capture may be absent */ }
        this._toggleLatch(entry.btn, entry.key);
      }, LATCH_HOLD_MS);
      this._active.set(pointerId, { kind: 'dualLatch', btn, key, timer });
    });
    btn.addEventListener('pointerup', (e) => this._releaseDualLatch(e.pointerId));
    btn.addEventListener('pointercancel', (e) => this._cancelDualLatch(e.pointerId));
    btn.addEventListener('pointerleave', (e) => {
      if (!btn.hasPointerCapture || !btn.hasPointerCapture(e.pointerId)) {
        this._cancelDualLatch(e.pointerId);
      }
    });
  }

  _pressMomentary(btn, key, pointerId = -1) {
    if (this._active.has(pointerId)) return;
    const autoMods = [...this._latched.values()].filter((m) => m.auto);
    const release = this.machine.matrixHold(key.y, key.x, MIN_HOLD_MS);
    btn.classList.add('is-down');
    this._active.set(pointerId, { kind: 'momentary', release, autoMods, btn });
  }

  _releaseMomentary(pointerId = -1) {
    const entry = this._active.get(pointerId);
    if (!entry) return;
    if (entry.kind === 'pendingMomentary') {
      this._active.delete(pointerId);
      entry.btn.classList.remove('is-down');
      this._pressMomentary(entry.btn, entry.key, pointerId);
      window.setTimeout(() => this._releaseMomentary(pointerId), MIN_HOLD_MS);
      return;
    }
    this._active.delete(pointerId);
    entry.btn.classList.remove('is-down');
    entry.release();
    for (const m of entry.autoMods) {
      if (this._latched.has(m.key)) this._unlatch(m);
    }
  }

  _cancelMomentary(pointerId = -1) {
    const entry = this._active.get(pointerId);
    if (!entry) return;
    if (entry.kind === 'pendingMomentary') {
      this._active.delete(pointerId);
      entry.btn.classList.remove('is-down');
      return;
    }
    this._releaseMomentary(pointerId);
  }

  _releaseDualLatch(pointerId = -1) {
    const entry = this._active.get(pointerId);
    if (!entry || entry.kind !== 'dualLatch') return;
    this._active.delete(pointerId);
    window.clearTimeout(entry.timer);
    entry.btn.classList.remove('is-down');
    if (this._latched.has(cellOf(entry.key))) {
      this._toggleLatch(entry.btn, entry.key);
      return;
    }
    this._pressMomentary(entry.btn, entry.key, pointerId);
    window.setTimeout(() => this._releaseMomentary(pointerId), MIN_HOLD_MS);
  }

  _cancelDualLatch(pointerId = -1) {
    const entry = this._active.get(pointerId);
    if (!entry || entry.kind !== 'dualLatch') return;
    this._active.delete(pointerId);
    window.clearTimeout(entry.timer);
    entry.btn.classList.remove('is-down');
  }

  _toggleLatch(btn, key) {
    const cell = cellOf(key);
    const cur = this._latched.get(cell);
    if (cur) { this._unlatch(cur); return; }
    const auto = key.latchAuto ?? !!key.mod;
    const release = this.machine.matrixHold(key.y, key.x, MIN_HOLD_MS);
    this._setCellLatched(cell, true);
    this._latched.set(cell, { key: cell, y: key.y, x: key.x, auto, el: btn, release });
  }

  _unlatch(m) {
    m.release();
    this._setCellLatched(m.key, false);
    this._latched.delete(m.key);
  }

  _setCellLatched(cell, on) {
    for (const info of this._keyInfos) {
      if (cellOf(info.key) !== cell) continue;
      info.el.classList.toggle('is-latched', on);
      if (info.el.classList.contains('is-latchable')) {
        info.el.setAttribute('aria-pressed', String(on));
      }
    }
  }

  _loadVisible() {
    try {
      const v = localStorage.getItem('ms2401.softkbd');
      if (v === null) return true;
      return v === '1';
    } catch { return true; }
  }

  _saveVisible() {
    try { localStorage.setItem('ms2401.softkbd', this._visible ? '1' : '0'); }
    catch { /* private mode / disabled storage */ }
  }

  _applyVisible() {
    document.documentElement.classList.toggle('kbd-open', this._visible);
    this.el.classList.toggle('is-open', this._visible);
    this.el.setAttribute('aria-hidden', String(!this._visible));
    for (const cb of this._toggleCbs || []) cb(this._visible);
  }

  get visible() { return this._visible; }

  setVisible(on) {
    on = !!on;
    if (on === this._visible) return;
    this._visible = on;
    if (!on) this._releaseAll();
    this._saveVisible();
    this._applyVisible();
  }

  toggle() { this.setVisible(!this._visible); }

  onToggle(cb) { (this._toggleCbs ||= []).push(cb); cb(this._visible); }

  _releaseAll() {
    for (const [, entry] of this._active) {
      entry.btn.classList.remove('is-down');
      if (entry.timer) window.clearTimeout(entry.timer);
      if (entry.release) entry.release();
    }
    this._active.clear();
    for (const m of [...this._latched.values()]) this._unlatch(m);
  }

  softKey(labelOrY, x, holdMs = 300) {
    let cellInfo = null;
    if (typeof labelOrY === 'number' && typeof x === 'number') {
      cellInfo = this._byCell.get(`${labelOrY},${x}`);
    } else {
      cellInfo = this._findByLabel(String(labelOrY));
    }
    if (!cellInfo) return false;
    const { el, key } = cellInfo;
    if (key.mod || key.latch || (key.dualLatch && holdMs >= LATCH_HOLD_MS)) {
      this._toggleLatch(el, key);
      return true;
    }
    if (key.dualLatch && this._latched.has(cellOf(key))) {
      this._toggleLatch(el, key);
      return true;
    }
    const pid = -Math.floor(Math.random() * 1e9) - 2;
    this._pressMomentary(el, key, pid);
    setTimeout(() => this._releaseMomentary(pid), holdMs);
    return true;
  }

  _findByLabel(label) {
    const want = label.trim().toLowerCase();
    const aliases = { print: '10,3', prtsc: '10,3', 'print screen': '10,3' };
    if (aliases[want]) return this._byCell.get(aliases[want]);
    for (const info of this._keyInfos) {
      for (const candidate of this._labelsFor(info.key)) {
        if (candidate === label) return info;
        if (candidate.toLowerCase() === want) return info;
      }
    }
    return null;
  }
}
